import { Icon } from '@natera/material/lib/icon'
import { memo, useLayoutEffect, useState } from 'react'
import { cn } from '~/shared/lib/cn'
import { Text } from '~/shared/ui/text'
import { CommandItem } from './command'
import type { Option } from './types'

/** Insert zero-width spaces after separator chars so labels wrap at symbol boundaries. */
function withSoftBreaks(text: string): string {
  return text.replace(/([/,;])/g, '$1​')
}

function formatText(text: string, searchValue?: string): React.ReactNode {
  text = withSoftBreaks(text)
  if (searchValue) {
    const regex = new RegExp(`(${searchValue})`, 'gi')
    const lines = text.split('\n')
    return lines
      .map((line) => {
        const parts = line.split(regex)
        return parts.map((part, i) =>
          regex.test(part) ? (
            <mark
              key={i}
              className="bg-secondary-focused rounded-xs text-[#5c8c0c]"
            >
              {part}
            </mark>
          ) : (
            part
          ),
        )
      })
      .map((line, index) => <div key={index}>{line}</div>)
  } else {
    const lines = text.split('\n')
    return lines.map((line, index) => <div key={index}>{line}</div>)
  }
}

function MultiSelectOption<T>({
  option,
  isSelected,
  toggleOption,
  dense = false,
  recommended = false,
  searchValue,
  hideCheckbox = false,
}: {
  option: Option<T>
  isSelected: boolean
  toggleOption: (value: T) => void
  dense?: boolean
  recommended?: boolean
  searchValue?: string
  hideCheckbox?: boolean
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  // If the search value is in the description, expand the option
  useLayoutEffect(() => {
    if (
      searchValue &&
      !option.label.toLowerCase().includes(searchValue.toLowerCase().trim()) &&
      option.description?.toLowerCase().includes(searchValue.toLowerCase().trim())
    ) {
      setIsExpanded(true)
    }
  }, [searchValue, option.label, option.description])

  // If the search value is empty, collapse the option
  useLayoutEffect(() => {
    if (!searchValue) {
      setIsExpanded(false)
    }
  }, [searchValue])

  return (
    <CommandItem
      className={cn({
        'bg-primary-selected': isSelected || recommended,
        'py-[3px]': dense,
      })}
      onSelect={() => toggleOption(option.value)}
    >
      <div className="flex flex-1 items-center gap-3">
        {!hideCheckbox && (
          <Icon
            className={cn('text-content-high-emphasis text-[18px]', {
              'text-primary-main': isSelected,
            })}
            children={isSelected ? 'check_box' : 'check_box_outline_blank'}
            fill={isSelected}
          />
        )}
        {hideCheckbox && isSelected && (
          <Icon
            className="text-primary-main text-[18px]"
            children="check"
          />
        )}
        <div className="flex flex-col py-0.5">
          <span className="line-clamp-1 text-[15px] leading-5 tracking-[0.25px]">
            {formatText(option.label, searchValue)}
          </span>
          {option.description && isExpanded ? (
            <Text
              appearance="CAPTION"
              className="text-text-secondary [&_span]:block"
            >
              {formatText(option.description, searchValue)}
            </Text>
          ) : null}
        </div>
      </div>
      {option.description ? (
        <button
          className="hover:bg-primary-selected focus-visible:bg-primary-selected mt-[3px] cursor-pointer self-start rounded-full border-none bg-transparent p-0 transition-colors outline-none"
          onClick={(e) => {
            e.stopPropagation()
            setIsExpanded(!isExpanded)
          }}
        >
          <Icon
            className={cn('text-text-secondary -rotate-90 text-[18px]', {
              'rotate-0': isExpanded,
            })}
            children="arrow_drop_down"
          />
        </button>
      ) : null}
    </CommandItem>
  )
}

export const MultiSelectOptionMemoized = memo(MultiSelectOption) as typeof MultiSelectOption
